import type { FetchError } from "ofetch";

export type LogInRequest = {
    UserName: string;
    Password: string;
};

export type LogInResponse = {
    ReturnUrl: string;
    AntiForgeryToken: string;
    RequiresMfa: boolean;
    Session: string;
};

export type MfaVerifyRequest = {
    UserName: string;
    Session: string;
    MfaCode: string;
};

export type RegisterRequest = {
    UserName: string;
    Name: string;
    CompanyName?: string;
    Email: string;
    Password: string;
    ConfirmPassword: string;
    TermsAndConditions: boolean;
};

export type RegisterResponse = {
    IsSuccess: boolean;
};

export type ForgotPasswordRequest = {
    Email: string;
};

export type ForgotPasswordResponse = {
    IsEmailSent: boolean;
};

export type UserConfirmationRequest = {
    Code: string;
};

export type ResetPasswordRequest = {
    Code: string;
    Username: string;
    NewPassword: string;
};

export type ResetPasswordResponse = {
    IsSuccess: boolean;
};

export type ConfirmUserResponse = {
    IsSuccess: boolean;
};

export type TotpSetupResponse = {
    SecretCode: string;
    QrCodeUri: string;
};

export type TotpStatusResponse = {
    Enabled: boolean;
};

export default function useIdentity() {
    const { $api } = useNuxtApp();
    const isAuthenticated = useStateIsAuthenticated();
    // Resist the urge to set isAuthenticated global state here, instead allow useInitializeState to take care of it.

    async function logIn(logInRequest: LogInRequest, returnUrl?: string) {
        try {
            const loginResponse = await $api<LogInResponse>("identity/sign-in", {
                method: "POST",
                body: logInRequest,
                query: { returnUrl },
            });

            return loginResponse;
        } catch (err) {
            const fetchError = err as FetchError;
            throw new Error(fetchError.data.Error);
        }
    }

    async function verifyMfa(mfaRequest: MfaVerifyRequest, returnUrl?: string) {
        try {
            const response = await $api<LogInResponse>("identity/mfa-verify", {
                method: "POST",
                body: mfaRequest,
                query: { returnUrl },
            });

            return response;
        } catch (err) {
            const fetchError = err as FetchError;
            throw new Error(fetchError.data.Error);
        }
    }

    function logOut() {
        if (!isAuthenticated.value) return;

        try {
            return $api("identity/logout");
        } catch (err) {
            // session cookie possibly destroyed in another tab, assume already signed out and swallow error
            console.error(err);
        }
    }

    async function register(registerRequest: RegisterRequest) {
        try {
            const registerResponse = await $api<RegisterResponse>("account/register", {
                method: "POST",
                body: registerRequest,
            });

            return registerResponse;
        } catch (err) {
            const fetchError = err as FetchError;
            throw new Error(fetchError.data.Error);
        }
    }

    async function forgotPassword(forgotPasswordRequest: ForgotPasswordRequest) {
        try {
            const forgotPasswordResponse = await $api<ForgotPasswordResponse>("account/forgot-password", {
                method: "POST",
                body: forgotPasswordRequest,
            });

            return forgotPasswordResponse;
        } catch (err) {
            const fetchError = err as FetchError;
            throw new Error(fetchError.data.Error);
        }
    }

    async function resetPassword(resetPasswordRequest: ResetPasswordRequest) {
        try {
            const resetPasswordResponse = await $api<ResetPasswordResponse>("account/reset-password", {
                method: "POST",
                body: resetPasswordRequest,
            });

            return resetPasswordResponse;
        } catch (err) {
            const fetchError = err as FetchError;
            throw new Error(fetchError.data.Error);
        }
    }

    async function confirmUser(userConfirmationRequest: UserConfirmationRequest) {
        try {
            const confirmUserResponse = await $api<ConfirmUserResponse>("account/confirm", {
                method: "POST",
                body: userConfirmationRequest,
            });

            return confirmUserResponse;
        } catch (err) {
            const fetchError = err as FetchError;
            throw new Error(fetchError.data.Error);
        }
    }

    async function getTotpStatus() {
        try {
            return await $api<TotpStatusResponse>("account/totp/status");
        } catch (err) {
            const fetchError = err as FetchError;
            throw new Error(fetchError.data.Error);
        }
    }

    async function setupTotp() {
        try {
            return await $api<TotpSetupResponse>("account/totp/setup", {
                method: "POST",
            });
        } catch (err) {
            const fetchError = err as FetchError;
            throw new Error(fetchError.data.Error);
        }
    }

    async function verifyTotp(code: string) {
        try {
            return await $api<{ Success: boolean }>("account/totp/verify", {
                method: "POST",
                body: { Code: code },
            });
        } catch (err) {
            const fetchError = err as FetchError;
            throw new Error(fetchError.data.Error);
        }
    }

    async function disableTotp() {
        try {
            return await $api<{ Success: boolean }>("account/totp/disable", {
                method: "POST",
            });
        } catch (err) {
            const fetchError = err as FetchError;
            throw new Error(fetchError.data.Error);
        }
    }

    return {
        logIn,
        verifyMfa,
        logOut,
        register,
        forgotPassword,
        resetPassword,
        confirmUser,
        getTotpStatus,
        setupTotp,
        verifyTotp,
        disableTotp,
    };
}
